/*
 * © 2026 Ecsper Ltd. All rights reserved.
 * SPDX-License-Identifier: LicenseRef-Ecsper-Proprietary
 * Unauthorised use, copying, or distribution is strictly prohibited.
 */

/**
 * Header names used by the Ecsper add-in HTTP client.
 *
 * Per CTR-PL-MS-OFFICESSO-001 v1.0.0 §3.1 (request headers) and §5.1 (response
 * headers), and the cross-group contracts digest CTR-MS365-CONTRACTS-v1.0.0
 * §"GUC Variables Set Per Request" (request id mapping).
 */

/** Per-request correlation id. Sent on every request, echoed by Platform. */
export const HEADER_REQUEST_ID = 'X-Request-ID';

/** Client identification, value `{clientId}/{clientVersion}` (e.g. `ecsper-office-addin/0.1.0`). */
export const HEADER_CLIENT = 'X-Ecsper-Client';

/** Residency region; emitted by the server, not by the client. */
export const HEADER_REGION = 'X-Ecsper-Region';

/** Standard HTTP authorisation header, value `Bearer {token}`. */
export const HEADER_AUTHORIZATION = 'Authorization';

/** JSON content type for serialised request and response bodies. */
export const HEADER_CONTENT_TYPE = 'Content-Type';

/** Standard `Retry-After` response header (seconds or HTTP-date). */
export const HEADER_RETRY_AFTER = 'Retry-After';

/** MIME type used for serialised JSON request bodies. */
export const CONTENT_TYPE_JSON = 'application/json';

/**
 * HTTP status codes that may be retried per CTR-MS365-CONTRACTS §Rate Limiting
 * and standard transient-server-error semantics.
 *
 * - `429` Too Many Requests (rate limited; respect `Retry-After`)
 * - `502` Bad Gateway
 * - `503` Service Unavailable
 * - `504` Gateway Timeout
 */
export const RETRYABLE_STATUS_CODES: readonly number[] = [429, 502, 503, 504];

/** Default per-request timeout in milliseconds. */
export const DEFAULT_TIMEOUT_MS = 30_000;

/** Default maximum retry attempts on retryable failures. */
export const DEFAULT_MAX_RETRIES = 3;

/** Initial exponential backoff window in milliseconds. */
export const INITIAL_BACKOFF_MS = 500;

/** Maximum exponential backoff window in milliseconds. */
export const MAX_BACKOFF_MS = 10_000;

/** Maximum random jitter added per attempt, in milliseconds. */
export const MAX_JITTER_MS = 100;
